/* Thẻ "Xác nhận hành động" — dùng chung cho bong bóng chat và Hộp duyệt.
   Mọi chữ đi qua textContent (không innerHTML) vì đến từ mô hình. Nút bấm chỉ mang
   data-prop-id / data-prop-do; controller bắt sự kiện bằng uỷ quyền. */
import type { ActionProposal } from "../types.ts";
import { el } from "./dom.ts";
import { icon } from "./icons.ts";

/* giờ Việt Nam, tự định dạng (Intl "vi-VN" khác nhau giữa các bản ICU) */
const fmtTime = (at: number) => {
  const d = new Date(at + 7 * 3600e3);
  const p = (n: number) => String(n).padStart(2, "0");
  return p(d.getUTCHours()) + ":" + p(d.getUTCMinutes()) + " " + p(d.getUTCDate()) + "/" + p(d.getUTCMonth() + 1);
};

export function stateLabel(p: ActionProposal): { text: string; tone: "warn" | "ok" | "bad" | "muted" | "info" } {
  switch (p.state) {
    case "proposed":
      return { text: "Chờ bạn xác nhận", tone: "warn" };
    case "awaiting_approval":
      return { text: "Đang chờ " + (p.approverLabel || "người duyệt") + " duyệt" + (p.deadline ? " · hạn " + fmtTime(p.deadline) : ""), tone: "info" };
    case "need_info":
      return { text: "Người duyệt cần bổ sung thông tin", tone: "warn" };
    case "done":
      return { text: (p.type === "gui_email" ? "Đã gửi" : p.type === "dat_lich_bao_cao" ? "Đã bật lịch" : "Đã thực hiện") + (p.record ? " · " + p.record : ""), tone: "ok" };
    case "rejected":
      return { text: "Bị từ chối", tone: "bad" };
    case "sending":
      return { text: "Đang gửi…", tone: "info" };
    case "failed":
      return { text: "Gửi thất bại" + (p.error ? ": " + p.error : ""), tone: "bad" };
    default:
      return { text: "Đã huỷ — không thay đổi gì", tone: "muted" };
  }
}

function text<K extends keyof HTMLElementTagNameMap>(tag: K, cls: string, value: string): HTMLElementTagNameMap[K] {
  const node = el(tag, cls);
  node.textContent = value;
  return node;
}

/**
 * @param mode "chat": nút Xác nhận/Huỷ cho người đề xuất. "inbox": chỉ hiển thị (nút duyệt do Hộp duyệt vẽ).
 */
export function renderProposal(p: ActionProposal, mode: "chat" | "inbox" = "chat"): HTMLElement {
  const st = stateLabel(p);
  const card = el("div", "apx-prop apx-prop-" + p.state + (p.blocked && p.state === "proposed" ? " apx-prop-blocked" : ""), { "data-prop": p.id });

  const head = el("div", "apx-prop-head");
  const hi = el("span", "apx-prop-ico");
  hi.innerHTML = icon(p.state === "done" ? "check" : p.state === "proposed" ? "warn" : "shield", 15);
  head.append(hi, text("span", "apx-prop-kind", p.state === "proposed" ? "Xác nhận hành động" : p.label || "Hành động"));
  if (p.demo) head.append(text("span", "apx-prop-demo", "Demo"));
  card.append(head);

  const body = el("div", "apx-prop-body");
  body.append(text("div", "apx-prop-title", p.title));
  if (p.label || p.target) body.append(text("div", "apx-prop-meta", [p.label, p.target].filter(Boolean).join(" · ")));

  /* thư đang chờ xác nhận: người nhận / CC / tiêu đề SỬA ĐƯỢC ngay trên thẻ (giọng nói có thể nghe sai địa chỉ) */
  const editable = mode === "chat" && p.type === "gui_email" && p.state === "proposed";
  const EDIT: Record<string, string> = { "Người nhận": "den", CC: "cc", "Tiêu đề": "tieu_de" };
  if (p.fields.length) {
    const dl = el("dl", "apx-prop-fields");
    const rows = editable && !p.fields.some(([k]) => k === "CC") ? [...p.fields.slice(0, 2), ["CC", ""] as [string, string], ...p.fields.slice(2)] : p.fields;
    for (const [k, v] of rows) {
      const row = el("div", "apx-prop-row");
      const key = EDIT[k];
      if (editable && key) {
        const dd = el("dd", "");
        const value = v === "—" ? "" : v;
        const input = el("input", "apx-prop-edit", { type: "text", value, "data-edit": key, "data-orig": value, "aria-label": k, spellcheck: "false", autocomplete: "off", ...(key === "tieu_de" ? {} : { inputmode: "email", autocapitalize: "off", placeholder: key === "cc" ? "(không bắt buộc)" : "ten@congty.com" }) }) as HTMLInputElement;
        input.value = value;
        dd.append(input);
        row.append(text("dt", "", k), dd);
      } else row.append(text("dt", "", k), text("dd", "", v));
      dl.append(row);
    }
    body.append(dl);
    if (editable) body.append(text("p", "apx-prop-note", "Sai địa chỉ hay tiêu đề? Sửa trực tiếp ở trên — Ami kiểm tra lại trước khi gửi."));
  }
  if (p.reason) body.append(text("p", "apx-prop-reason", "Căn cứ: " + p.reason));
  if (p.checks && p.checks.length) {
    const box = el("div", "apx-checks");
    box.append(text("div", "apx-checks-title", "Kiểm tra trước khi ghi (đối chiếu dữ liệu ERP)"));
    for (const c of p.checks) {
      const row = el("div", "apx-check apx-check-" + c.status);
      const ic = el("span", "apx-check-ico");
      ic.innerHTML = icon(c.status === "ok" ? "check" : c.status === "block" ? "close" : "warn", 12, 2.6);
      const t = el("span", "");
      t.append(text("b", "", c.label + ": "), document.createTextNode(c.detail));
      /* "Có phải ý bạn là …" ⇒ một nút thay địa chỉ trên thẻ (chỉ khi thẻ còn sửa được) */
      if (c.suggest && editable) {
        const use = el("button", "apx-btn apx-btn-ghost apx-prop-suggest", { type: "button", "data-suggest-from": c.suggest.from, "data-suggest-to": c.suggest.to });
        use.textContent = "Dùng " + c.suggest.to;
        t.append(document.createTextNode(" "), use);
      }
      row.append(ic, t);
      box.append(row);
    }
    body.append(box);
  }
  if (p.overrideNote) body.append(text("p", "apx-prop-reason", "Lý do ngoại lệ: " + p.overrideNote));
  for (const w of p.warnings || []) {
    const warn = el("p", "apx-prop-warn");
    warn.innerHTML = icon("warn", 13);
    warn.append(document.createTextNode(" " + w));
    body.append(warn);
  }
  if (p.policy) {
    const pol = el("p", "apx-prop-policy");
    pol.innerHTML = icon("shield", 13);
    pol.append(
      document.createTextNode(
        " " + (p.needsApproval ? "Cần " + (p.approverLabel || "người có thẩm quyền") + " duyệt · " : p.selfApproved ? "Bạn có thẩm quyền duyệt cấp này · " : "") + p.policy,
      ),
    );
    body.append(pol);
  }

  const status = el("div", "apx-prop-status apx-tone-" + st.tone);
  status.textContent = st.text;

  /* thư và lịch báo cáo không có đường "duyệt ngoại lệ": bị chặn thì chỉ bỏ thẻ, nhờ Ami soạn lại */
  const hardBlocked = (p.type === "gui_email" || p.type === "dat_lich_bao_cao") && p.blocked;
  if (mode === "chat" && hardBlocked && p.state === "proposed") {
    body.append(text("p", "apx-prop-note", (p.type === "gui_email" ? "Thư này chưa gửi được" : "Lịch này chưa bật được") + " — xem mục bị chặn ở trên, rồi nhờ Apus Ami soạn lại."));
    const row = el("div", "apx-prop-buttons");
    const cancel = el("button", "apx-btn apx-btn-ghost", { type: "button", "data-prop-id": p.id, "data-prop-do": "cancel" });
    cancel.textContent = p.type === "gui_email" ? "Bỏ thư này" : "Bỏ thẻ này";
    row.append(cancel);
    body.append(row);
  } else if (mode === "chat" && (p.state === "proposed" || p.state === "need_info")) {
    if (p.demo) body.append(text("p", "apx-prop-note", "Bản demo: xác nhận sẽ tạo số chứng từ giả, chưa ghi vào ERP thật."));
    if (p.type === "gui_email") body.append(text("p", "apx-prop-note", "Thư sẽ được GỬI THẬT ngay khi bạn bấm Xác nhận."));
    if (p.type === "dat_lich_bao_cao") body.append(text("p", "apx-prop-note", "Xác nhận là bật lịch; nếu có địa chỉ, mỗi lần chạy sẽ GỬI THẬT email. Tạm dừng hoặc xoá ở tab Báo cáo."));
    if (p.blocked) {
      body.append(
        el("textarea", "apx-note apx-prop-override", { rows: "2", maxlength: "400", "aria-label": "Lý do ngoại lệ", placeholder: "Lý do vẫn đề xuất dù có mục chặn (bắt buộc), ví dụ: khách đổi lịch, cần lệnh bổ sung…" }),
      );
    }
    const row = el("div", "apx-prop-buttons");
    const cancel = el("button", "apx-btn apx-btn-ghost", { type: "button", "data-prop-id": p.id, "data-prop-do": "cancel" });
    cancel.textContent = "Huỷ";
    const ok = el("button", "apx-btn apx-btn-primary", { type: "button", "data-prop-id": p.id, "data-prop-do": "confirm" });
    ok.innerHTML = icon("check", 14);
    ok.append(document.createTextNode(p.type === "gui_email" ? " Xác nhận & gửi thư" : p.type === "dat_lich_bao_cao" ? " Xác nhận & bật lịch" : p.state === "need_info" ? " Gửi lại để duyệt" : p.blocked ? " Gửi duyệt ngoại lệ" : p.needsApproval ? " Xác nhận & gửi duyệt" : " Xác nhận"));
    row.append(cancel, ok);
    if (p.state === "need_info") body.append(status);
    body.append(row);
  } else {
    body.append(status);
  }

  if (p.history && p.history.length > 1) {
    const det = el("details", "apx-prop-history");
    det.append(text("summary", "", "Lịch sử (" + p.history.length + ")"));
    const ol = el("ol", "");
    for (const h of p.history) ol.append(text("li", "", fmtTime(h.at) + " · " + h.by + " — " + h.what));
    det.append(ol);
    body.append(det);
  }

  card.append(body);
  return card;
}
